import Logger from '@utils/log'
import { useState, useCallback, useEffect } from 'react'
import { useWire } from '@forminator/react-wire'
import * as store from '@store'
import { noop } from '@utils'

const log = new Logger('useRenderPdf', { enabled: false })

/**
 * Custom React hook for rendering a PDF onto a canvas.
 *
 * Warning: if this causes an infinite loop, make sure the `onError`
 * method is a memoized function (`useCallback`, etc. should work)
 * 
 * The general flow for this hook is:
 * 1. Initialize the PDF rendering library (global worker, etc.)
 * 2. Load the PDF file (async, using the PDF library)
 * 3. Render the PDF onto a given canvas
 *
 * #gpt
 *
 * @function
 * @param {Object} options - The options for rendering the PDF
 * @param {boolean} [options.enabled=true] - Whether to render the PDF
 * @param {string} options.url - The URL of the PDF to render
 * @param {number} [options.pageIndex=0] - The index of the page to render
 * @param {function} options.setPageCount - Function to set the total number of pages in the PDF
 * @param {Object} options.refCanvas - Reference to the canvas element where the PDF will be rendered
 * @param {function} [options.onError=noop] - Function to call when an error occurs during rendering
 * @param {function} [options.onBeforeRender] - Function to call before rendering the PDF
 * @param {function} [options.onAfterRender] - Function to call after rendering the PDF
 * @returns {Object} An object containing the page count and functions to set the page count and loading state
 * @returns {number} pageCount - The total number of pages in the PDF
 * @returns {function} setPageCount - Function to set the total number of pages in the PDF
 * @returns {boolean} loading - Whether the PDF is currently being rendered
 */
export const useRenderPdf = ({
    enabled = true,
    url,
    pageIndex = 0,
    setPageCount,
    getCanvas,
    onError = noop,
    onBeforeRender,
    onAfterRender,
} = {}) => {
    
    //region State
    
    const [isInit, setIsInit] = useState(false)
    const [pdf, setPdf] = useState(null)
    
    const loadingDoc = useWire(store.pdfRendererLoadingDoc)
    const loadingPage = useWire(store.pdfRendererLoadingPage)
    
    //endregion State
    
    //region Methods
    
    /**
     * Loads a PDF document by URL
     * @param {String} url
     * @returns {Promise<void>}
     */
    const loadDocument = useCallback(async url => {
        
        log.d('loadDocument')
        loadingDoc.setValue(true)
        
        try {
            
            const { pdfjsLib } = globalThis
            
            // @todo not sure how to import the types for pdfJsLib
            // noinspection JSCheckFunctionSignatures
            const loadingTask = pdfjsLib.getDocument({ url })
            const loadedPdf = await loadingTask.promise
            
            log.d('loaded pdf,', loadedPdf.numPages, 'pages')
            setPdf(loadedPdf)
            setPageCount?.(loadedPdf.numPages)
            
        } catch (e) {
            
            log.e('failed', e)
            onError?.(e)
            
        }
        
        loadingDoc.setValue(false)
        
    }, [onError, loadingDoc, setPageCount])
    
    /**
     * Renders a page from a PDF onto a canvas
     * 
     * @param {PDFDocument} pdf
     * @returns {Promise<void>}
     */
    const renderPage = useCallback(async pdf => {
        
        log.d('renderPage')
        loadingPage.setValue(true)
        
        try {
            
            log.d('renderPage rendering page')
            
            // Note: the PDF library explicitly uses page *number* not index
            const page = await pdf.getPage(pageIndex + 1)
            
            onBeforeRender?.()
            
            const viewport = page.getViewport({ scale: 1 })
            const canvas = getCanvas()
            
            canvas.width = viewport?.width ?? 0
            canvas.height = viewport?.height ?? 0
            
            const renderContext = {
                canvasContext: canvas.getContext('2d'),
                viewport,
            }
            
            const renderTask = page.render(renderContext)
            
            await renderTask.promise
            
            log.d('renderPage rendering complete')
            onAfterRender?.()
            
        } catch (e) {
            
            log.e('renderPage failed', e)
            
        }
        
        loadingPage.setValue(false)
        
    }, [loadingPage, pageIndex, onBeforeRender, getCanvas, onAfterRender])
    
    //endregion Methods
    
    /**
     * Initialize PDF library hook
     */
    useEffect(() => {
        
        if (!enabled || !getCanvas()) return
        log.d('hook initialize')
        
        const { pdfjsLib } = globalThis
        
        pdfjsLib.GlobalWorkerOptions.workerSrc = 'pdfjs-dist/build/pdf.worker.js'
        setIsInit(true)
        
        return () => {
            log.d('hook initialize cleanup')
            setPdf(null)
        }
        
    }, [enabled, getCanvas, setIsInit, onError])
    
    /**
     * Load PDF document hook
     */
    useEffect(() => {
        
        if (!isInit) return
        log.d('hook load pdf')
        
        // noinspection JSIgnoredPromiseFromCall
        loadDocument(url)
        
    }, [isInit, loadDocument, url])
    
    /**
     * Render PDF hook
     */
    useEffect(() => {
        
        if (!pdf) return
        log.d('hook render pdf')
        
        // noinspection JSIgnoredPromiseFromCall
        renderPage(pdf)
        
    }, [pdf, renderPage])
    
}
